import { format, isSameDay, subDays } from "date-fns";

import type { Encounter } from "@elation/api-client/src/stitch-web/models/encounter";
import type { EncounterListItem } from "@elation/api-client/src/stitch-web/models/encounterListItem";
import { EncounterStatus } from "@elation/api-client/src/stitch-web/models/encounterStatus";
import type { PaginatedEncounterListItem } from "@elation/api-client/src/stitch-web/models/paginatedEncounterListItem";

import { parseNullableDate } from "@src/shared/utils/dates";

type EncounterStatusValue = Encounter["status"];
type MaybeEncounter = Encounter | null | undefined;
type MaybeEncounterListItem = EncounterListItem | null | undefined;
type RecordingStep = string | null | undefined;

export type EncounterStage = "agenda" | "visit" | "note";
export type EncounterRecencyGroupId = "today" | "yesterday" | "earlier";

export interface EncounterRecencyGroup {
  id: EncounterRecencyGroupId;
  label: string;
  items: PaginatedEncounterListItem[];
}

interface EncounterStageInput {
  encounter: MaybeEncounter;
  recordingStep: RecordingStep;
  isEncounterFinalizing: boolean;
}

const VALID_TRANSITIONS: Record<
  EncounterStatusValue,
  readonly EncounterStatusValue[]
> = {
  [EncounterStatus.created]: [
    EncounterStatus.in_progress,
    EncounterStatus.finalizing,
    EncounterStatus.completed,
    EncounterStatus.closed,
  ],
  [EncounterStatus.in_progress]: [
    EncounterStatus.finalizing,
    EncounterStatus.completed,
    EncounterStatus.closed,
  ],
  [EncounterStatus.finalizing]: [
    EncounterStatus.in_progress,
    EncounterStatus.completed,
    EncounterStatus.closed,
  ],
  [EncounterStatus.completed]: [EncounterStatus.in_progress],
  [EncounterStatus.closed]: [EncounterStatus.in_progress],
};

const GROUPS: Array<Pick<EncounterRecencyGroup, "id" | "label">> = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "earlier", label: "Earlier" },
];

function normalizePreviewText(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function compactParts(parts: Array<string | null | undefined>): string {
  return parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
}

function groupIdForDate(date: Date | null, now: Date): EncounterRecencyGroupId {
  if (!date) return "earlier";
  if (isSameDay(date, now)) return "today";
  if (isSameDay(date, subDays(now, 1))) return "yesterday";
  return "earlier";
}

function shortTime(date: Date): string {
  const suffix = format(date, "a").toLowerCase().startsWith("a") ? "a" : "p";
  return `${format(date, "h:mm")}${suffix}`;
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

export const EncounterEntity = {
  canTransitionTo(
    encounter: MaybeEncounter,
    next: EncounterStatusValue,
  ): boolean {
    if (!encounter) return false;
    return VALID_TRANSITIONS[encounter.status].includes(next);
  },

  canComplete(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return (
      encounter.status === EncounterStatus.created ||
      encounter.status === EncounterStatus.in_progress ||
      encounter.status === EncounterStatus.finalizing
    );
  },

  canReopen(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return (
      encounter.status === EncounterStatus.completed ||
      encounter.status === EncounterStatus.closed
    );
  },

  isActive(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return (
      encounter.status === EncounterStatus.created ||
      encounter.status === EncounterStatus.in_progress
    );
  },

  isFinalizing(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return encounter.status === EncounterStatus.finalizing;
  },

  isCompleted(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return (
      encounter.status === EncounterStatus.completed ||
      encounter.status === EncounterStatus.closed
    );
  },

  isGeneratingNote(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    if (EncounterEntity.hasGeneratedNote(encounter)) return false;

    return EncounterEntity.isFinalizing(encounter);
  },

  stage({
    encounter,
    recordingStep,
    isEncounterFinalizing,
  }: EncounterStageInput): EncounterStage {
    if (
      isEncounterFinalizing ||
      (EncounterEntity.isCompleted(encounter) &&
        EncounterEntity.hasGeneratedNote(encounter))
    ) {
      return "note";
    }

    if (
      EncounterEntity.isGeneratingNote(encounter) ||
      recordingStep !== "initial"
    ) {
      return "visit";
    }

    return "agenda";
  },

  hasGeneratedNote(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return encounter.note != null;
  },

  hasNoteText(encounter: MaybeEncounter): boolean {
    return EncounterEntity.notePreview(encounter) != null;
  },

  notePreview(
    encounter: MaybeEncounter | MaybeEncounterListItem,
    maxLength = 160,
  ): string | null {
    if (!encounter) return null;
    const value =
      "notePreview" in encounter ? encounter.notePreview : encounter.note;
    if (value == null) return null;

    const normalized = normalizePreviewText(value);
    if (!normalized) return null;
    if (normalized.length <= maxLength) return normalized;

    return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
  },

  startedAt(encounter: MaybeEncounter): Date | null {
    return parseNullableDate(encounter?.startedAt);
  },

  finalizingAt(encounter: MaybeEncounter): Date | null {
    return parseNullableDate(encounter?.finalizingAt);
  },

  completedAt(encounter: MaybeEncounter): Date | null {
    return parseNullableDate(encounter?.completedAt);
  },

  lastLifecycleDate(encounter: MaybeEncounter): Date | null {
    return (
      EncounterEntity.completedAt(encounter) ??
      EncounterEntity.finalizingAt(encounter) ??
      EncounterEntity.startedAt(encounter)
    );
  },

  completedOrStartedAt(item: MaybeEncounterListItem): Date | null {
    return parseNullableDate(item?.completedAt ?? item?.startedAt);
  },

  visitDateLabel(item: MaybeEncounterListItem): string | null {
    const date = EncounterEntity.completedOrStartedAt(item);
    if (!date) return null;

    return new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  },

  physicianDisplayName(item: MaybeEncounterListItem): string | null {
    if (!item) return null;

    return (
      compactParts([item.physicianFirstName, item.physicianLastName]) || null
    );
  },

  patientVisitMetadata(item: MaybeEncounterListItem): string | null {
    const metadata = [
      EncounterEntity.visitDateLabel(item),
      EncounterEntity.physicianDisplayName(item),
    ]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(" · ");

    return metadata || null;
  },

  patientDisplayName(item: PaginatedEncounterListItem): string {
    return compactParts([
      item.patient.firstName,
      item.patient.middleName,
      item.patient.lastName,
    ]);
  },

  patientInitials(item: PaginatedEncounterListItem): string {
    const first = item.patient.firstName.trim().charAt(0);
    const last = item.patient.lastName.trim().charAt(0);
    const initials = `${first}${last}`.toUpperCase();

    return initials || "?";
  },

  startedAtDate(item: PaginatedEncounterListItem): Date | null {
    return parseNullableDate(item.startedAt);
  },

  startedAtRecencyLabel(
    item: PaginatedEncounterListItem,
    groupId: EncounterRecencyGroupId,
  ): string {
    const date = EncounterEntity.startedAtDate(item);
    if (!date) return "Time unavailable";

    if (groupId === "today") return `Today · ${shortTime(date)}`;
    if (groupId === "yesterday") return `Yesterday · ${shortTime(date)}`;
    return `${format(date, "EEE")} · ${shortTime(date)}`;
  },

  listMetadata(
    item: PaginatedEncounterListItem,
    groupId: EncounterRecencyGroupId,
  ): string {
    return [
      EncounterEntity.startedAtRecencyLabel(item, groupId),
      pluralize(item.agendaItemCount, "item"),
      pluralize(item.codeCount, "code"),
    ].join(" · ");
  },

  groupByRecency(
    items: PaginatedEncounterListItem[],
    now: Date = new Date(),
  ): EncounterRecencyGroup[] {
    const buckets: Record<
      EncounterRecencyGroupId,
      PaginatedEncounterListItem[]
    > = {
      today: [],
      yesterday: [],
      earlier: [],
    };

    for (const item of items) {
      buckets[groupIdForDate(EncounterEntity.startedAtDate(item), now)].push(
        item,
      );
    }

    return GROUPS.flatMap((group) => {
      const groupItems = buckets[group.id];
      return groupItems.length ? [{ ...group, items: groupItems }] : [];
    });
  },
} as const;
